import cloneDeep from 'lodash/cloneDeep'
import mergeWith from 'lodash/mergeWith'

import { success, failure, Result } from '../shared'
import { AppConfigProvider } from './appConfig.provider'
import { BaseAppConfig, AppConfigServiceErrorCodes } from './appConfig.types'

type AppConfigServiceResult<T> = Result<T>

export class AppConfigService<T extends BaseAppConfig> {
  private readonly defaults: Partial<T>

  constructor(defaults: Partial<T>, private customizers, private provider: AppConfigProvider) {
    this.defaults = cloneDeep(defaults)
  }

  private mergeDefaults(config): T {
    return mergeWith({}, this.defaults, config, (defaultValue, configValue, key) => {
      return this.customizers[key] && this.customizers[key](defaultValue, configValue)
    })
  }

  public async get(): Promise<AppConfigServiceResult<T>> {
    try {
      const { data } = await this.provider.get()

      return success(this.mergeDefaults(data))
    } catch (error) {
      if (error.hasOwnProperty('response')) {
        return failure(AppConfigServiceErrorCodes.CANNOT_LOAD_FILE)
      }

      if (error instanceof SyntaxError) {
        return failure(AppConfigServiceErrorCodes.CANNOT_PARSE_JSON)
      }

      return failure(AppConfigServiceErrorCodes.GENERIC)
    }
  }
}
